// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedplumClient, WithId } from '@medplum/core';
import type { Patient } from '@medplum/fhirtypes';
import { createPatientWithMrn, withPendingIntakeTag } from '@polarissleep/shared';
import { withTenantScope } from '../utils/tenant';

export interface CreateStaffPatientInput {
  firstName: string;
  lastName: string;
  email: string;
  birthDate: string;
}

export type CreateStaffPatientResult =
  | { status: 'created'; patient: WithId<Patient> }
  | { status: 'duplicates'; matches: Patient[] };

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

function normalizeName(value: string): string {
  return value.trim().toLocaleLowerCase();
}

function isLikelyDuplicate(patient: Patient, input: CreateStaffPatientInput): boolean {
  const email = patient.telecom?.find((contact) => contact.system === 'email')?.value;
  const name = patient.name?.[0];
  const sameEmail = email && normalizeEmail(email) === normalizeEmail(input.email);
  const sameNameAndBirthDate =
    normalizeName(name?.given?.[0] ?? '') === normalizeName(input.firstName) &&
    normalizeName(name?.family ?? '') === normalizeName(input.lastName) &&
    patient.birthDate === input.birthDate;
  return Boolean(sameEmail || sameNameAndBirthDate);
}

async function findLikelyDuplicates(medplum: MedplumClient, input: CreateStaffPatientInput): Promise<Patient[]> {
  const [emailMatches, nameMatches] = await Promise.all([
    medplum.searchResources('Patient', {
      'telecom:contains': normalizeEmail(input.email),
      _count: '20',
    }),
    medplum.searchResources('Patient', {
      'name:contains': `${input.firstName} ${input.lastName}`.trim(),
      birthdate: input.birthDate,
      _count: '20',
    }),
  ]);
  const unique = new Map<string, Patient>();
  for (const patient of [...(emailMatches as Patient[]), ...(nameMatches as Patient[])]) {
    if (patient.id && isLikelyDuplicate(patient, input)) {
      unique.set(patient.id, patient);
    }
  }
  return [...unique.values()];
}

export async function createStaffPatient(
  medplum: MedplumClient,
  input: CreateStaffPatientInput,
  allowDuplicate = false
): Promise<CreateStaffPatientResult> {
  const normalizedInput = {
    ...input,
    firstName: input.firstName.trim(),
    lastName: input.lastName.trim(),
    email: normalizeEmail(input.email),
  };
  const matches = await findLikelyDuplicates(medplum, normalizedInput);
  if (matches.length > 0 && !allowDuplicate) {
    return { status: 'duplicates', matches };
  }

  const patient = withTenantScope(
    withPendingIntakeTag({
      resourceType: 'Patient',
      name: [{ given: [normalizedInput.firstName], family: normalizedInput.lastName }],
      birthDate: normalizedInput.birthDate,
      telecom: [{ system: 'email', value: normalizedInput.email, use: 'home' }],
    })
  );
  const created = await createPatientWithMrn(medplum, patient, import.meta.env.VITE_POLARIS_ENV);
  return { status: 'created', patient: created };
}
